import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Toaster, toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const projectTypes = [
  { value: "recherche", label: "Recherche de bien" },
  { value: "vente", label: "Vente de mon bien" },
  { value: "valorisation", label: "Valorisation de mon bien" },
  { value: "estimation", label: "Estimation" }
];

export const ContactPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    nom: "",
    prenom: "",
    email: "",
    telephone: "",
    projet_type: "",
    message: ""
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, projet_type: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data) {
        toast.success("Message envoyé avec succès ! Nous vous recontacterons rapidement.");
        setFormData({
          nom: "",
          prenom: "",
          email: "",
          telephone: "",
          projet_type: "",
          message: ""
        });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-32">
      <Toaster position="top-center" richColors />
      
      {/* Hero */}
      <section className="py-16 md:py-24 bg-background">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <p className="text-gold text-sm font-bold uppercase tracking-[0.2em] mb-4">
              Parlons de votre projet
            </p>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-text-primary mb-6">
              Contact
            </h1>
            <p className="text-text-secondary text-lg">
              Remplissez le formulaire ci-dessous et nous vous recontacterons dans les plus brefs délais
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form */}
      <section data-testid="contact-form-section" className="py-16 md:py-24 bg-surface">
        <div className="max-w-2xl mx-auto px-6 lg:px-8">
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            onSubmit={handleSubmit}
            className="bg-white border border-line p-8 md:p-12 space-y-6 shadow-sm"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-text-primary font-semibold mb-2">
                  Nom *
                </label>
                <Input
                  data-testid="input-nom"
                  name="nom"
                  value={formData.nom}
                  onChange={handleChange}
                  required
                  className="bg-white border-line text-text-secondary focus:border-gold focus:ring-gold"
                  placeholder="Votre nom"
                />
              </div>
              <div>
                <label className="block text-text-primary font-semibold mb-2">
                  Prénom
                </label>
                <Input
                  data-testid="input-prenom"
                  name="prenom"
                  value={formData.prenom}
                  onChange={handleChange}
                  className="bg-white border-line text-text-secondary focus:border-gold focus:ring-gold"
                  placeholder="Votre prénom"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-text-primary font-semibold mb-2">
                  Email *
                </label>
                <Input
                  data-testid="input-email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="bg-white border-line text-text-secondary focus:border-gold focus:ring-gold"
                  placeholder="votre@email.com"
                />
              </div>
              <div>
                <label className="block text-text-primary font-semibold mb-2">
                  Téléphone
                </label>
                <Input
                  data-testid="input-telephone"
                  type="tel"
                  name="telephone"
                  value={formData.telephone}
                  onChange={handleChange}
                  className="bg-white border-line text-text-secondary focus:border-gold focus:ring-gold"
                  placeholder="06 00 00 00 00"
                />
              </div>
            </div>

            <div>
              <label className="block text-text-primary font-semibold mb-2">
                Type de projet *
              </label>
              <Select
                value={formData.projet_type}
                onValueChange={handleSelectChange}
                required
              >
                <SelectTrigger
                  data-testid="select-projet"
                  className="bg-white border-line text-text-secondary focus:border-gold focus:ring-gold"
                >
                  <SelectValue placeholder="Sélectionnez votre projet" />
                </SelectTrigger>
                <SelectContent className="bg-white border-line">
                  {projectTypes.map((type) => (
                    <SelectItem
                      key={type.value}
                      value={type.value}
                      className="text-text-secondary hover:bg-gold/10 focus:bg-gold/10"
                    >
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-text-primary font-semibold mb-2">
                Message
              </label>
              <Textarea
                data-testid="input-message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={5}
                className="bg-white border-line text-text-secondary focus:border-gold focus:ring-gold resize-none"
                placeholder="Décrivez votre projet..."
              />
            </div>

            <Button
              data-testid="submit-button"
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-navy text-white hover:bg-navy hover:border-gold border border-navy font-semibold py-6 text-lg rounded-none"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                "Envoyer le message"
              )}
            </Button>

            <p className="text-text-secondary text-sm text-center">
              En soumettant ce formulaire, vous acceptez que vos données soient utilisées 
              pour vous recontacter dans le cadre de votre projet immobilier.
            </p>
          </motion.form>
        </div>
      </section>
    </div>
  );
};
